import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  ErrorState,
  Skeleton,
} from '@marrafa-gioale/gymx-ui-web'
import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowLeft, Ban, FlaskConical, TriangleAlert } from 'lucide-react'

import { ApiError, ERROR_CODES, errorMessage } from '@/api/errors'
import { useCancelImport, useImportJob } from '@/api/queries'
import type { ImportJob } from '@/api/types'
import { isImportRunning } from '@/api/types'
import { PageHeader } from '@/components/layout/app-shell'
import { ImportCounters, ImportProgress, ImportStatusBadge } from '@/features/imports/import-status'
import { notify } from '@/lib/toast'
import {
  formatDateTime,
  formatDuration,
  IMPORT_MODE_LABELS,
  IMPORT_SOURCE_LABELS,
  IMPORT_WARNING_LABELS,
  labelForCode,
} from '@/lib/format'

export const Route = createFileRoute('/_app/import/$jobId')({
  component: ImportDetailPage,
})

function ImportDetailPage() {
  const { jobId } = Route.useParams()
  // Il polling si accende solo se il job è attivo e si spegne da sé quando arriva in
  // uno stato terminale: il contract non prevede notifiche push.
  const query = useImportJob(jobId)

  if (query.isPending) {
    return (
      <>
        <PageHeader title="Import" />
        <div className="space-y-4">
          <Skeleton className="h-28 w-full rounded-lg" />
          <Skeleton className="h-40 w-full rounded-lg" />
        </div>
      </>
    )
  }

  // Un poll fallito **non** deve cancellare la pagina di avanzamento: l'errore
  // sostituisce il contenuto solo se non c'è ancora un job da mostrare. Altrimenti
  // resta l'ultimo stato noto, con un avviso sopra — il toast di refetch fallito lo
  // segnala già, e la pagina non lampeggia a ogni blip di rete.
  if (query.isError && !query.data) {
    const notFound = query.error instanceof ApiError && query.error.status === 404
    return (
      <>
        <PageHeader title="Import" actions={<BackButton />} />
        <ErrorState
          title={notFound ? 'Job non trovato' : 'Caricamento non riuscito'}
          message={errorMessage(query.error)}
          requestId={query.error instanceof ApiError ? query.error.requestId : undefined}
          {...(notFound ? {} : { onRetry: () => void query.refetch() })}
        />
      </>
    )
  }

  if (!query.data) return null

  return (
    <>
      {query.isError ? (
        <Alert variant="warning" title="Avanzamento non aggiornato" className="mb-4">
          L'ultimo aggiornamento non è riuscito ({errorMessage(query.error)}). I dati qui sotto sono
          l'ultimo stato ricevuto.
        </Alert>
      ) : null}
      <ImportDetail job={query.data} />
    </>
  )
}

function BackButton() {
  return (
    <Button variant="ghost" asChild>
      <Link to="/import">
        <ArrowLeft />
        Torna agli import
      </Link>
    </Button>
  )
}

function ImportDetail({ job }: { job: ImportJob }) {
  const cancel = useCancelImport()
  const active = isImportRunning(job)
  const warnings = job.warnings ?? []

  const onCancel = async () => {
    try {
      await cancel.mutateAsync(job.id)
      notify({
        message:
          'Import annullato. Gli esercizi già scritti restano: l’import non è transazionale.',
        variant: 'success',
      })
    } catch (error) {
      if (error instanceof ApiError && error.code === ERROR_CODES.importNotCancellable) {
        notify({ message: error.message, variant: 'danger' })
        return
      }
      notify({ message: errorMessage(error), variant: 'danger' })
    }
  }

  return (
    <>
      <PageHeader
        title={`Import da ${IMPORT_SOURCE_LABELS[job.source]}`}
        description={<code className="font-mono text-xs">{job.id}</code>}
        actions={
          <>
            {active ? (
              <Button
                variant="danger-outline"
                loading={cancel.isPending}
                onClick={() => void onCancel()}
              >
                <Ban />
                Annulla import
              </Button>
            ) : null}
            <BackButton />
          </>
        }
      />

      <div className="space-y-4">
        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-center gap-1.5">
              <ImportStatusBadge status={job.status} />
              <Badge variant={job.mode === 'replace' ? 'warning' : 'neutral'}>
                {IMPORT_MODE_LABELS[job.mode]}
              </Badge>
              {job.dryRun ? (
                <Badge variant="secondary">
                  <FlaskConical />
                  Prova
                </Badge>
              ) : null}
            </div>
          </CardHeader>

          <CardContent className="space-y-5">
            <ImportProgress job={job} />

            {job.dryRun ? (
              <Alert variant="info" title="Nessuna scrittura" icon={false}>
                È una prova: i conteggi qui sotto sono il piano che verrebbe applicato.
              </Alert>
            ) : null}

            <ImportCounters job={job} />

            {job.error ? (
              <Alert variant="danger" title={job.error.message}>
                <code className="font-mono text-xs">{job.error.code}</code>
                {job.error.code === ERROR_CODES.upstreamUnavailable ? (
                  <p className="mt-1">
                    Gli esercizi già scritti restano in catalogo: l'import è incrementale e non
                    transazionale. Si può rilanciare, e le righe identiche risulteranno invariate.
                  </p>
                ) : null}
              </Alert>
            ) : null}

            {job.status === 'cancelled' ? (
              <Alert variant="warning" title="Annullato a metà">
                Gli esercizi già scritti sono rimasti in catalogo. Verificane lo stato di
                pubblicazione prima di considerare chiusa l'operazione.
              </Alert>
            ) : null}

            {job.mode === 'replace' && job.status === 'succeeded' && !job.dryRun ? (
              <Alert variant="info" title="I client faranno un sync completo">
                La versione del catalogo è cambiata: al primo sync incrementale le app riceveranno{' '}
                <code className="font-mono text-xs">409 catalog_reset</code> e riscaricheranno
                tutto.
              </Alert>
            ) : null}
          </CardContent>
        </Card>

        <div className="grid gap-4 lg:grid-cols-[1fr_18rem]">
          {/* Avvisi: non bloccanti, ma sono la lista di cosa andare a correggere. */}
          <Card>
            <CardHeader>
              <div className="space-y-1">
                <CardTitle>Avvisi{warnings.length > 0 ? ` (${warnings.length})` : ''}</CardTitle>
                <CardDescription>
                  Anomalie non bloccanti. Ogni voce è un esercizio da rivedere prima di pubblicarlo.
                </CardDescription>
              </div>
            </CardHeader>
            <CardContent>
              {warnings.length === 0 ? (
                <p className="text-muted-foreground text-sm">
                  Nessun avviso: tutte le righe elaborate erano complete.
                </p>
              ) : (
                <ul className="max-h-96 scrollbar-thin space-y-2 overflow-y-auto">
                  {warnings.map((warning, index) => (
                    <li
                      key={`${warning.code}-${warning.externalId ?? index}`}
                      className="border-warning/30 bg-warning-soft flex gap-2.5 rounded-sm border p-2.5 text-xs"
                    >
                      <TriangleAlert
                        className="text-warning-content mt-px size-3.5 shrink-0"
                        aria-hidden
                      />
                      <div className="min-w-0 space-y-0.5">
                        <p className="font-medium">
                          {labelForCode(warning.code, IMPORT_WARNING_LABELS)}
                          {warning.externalId ? (
                            <span className="text-muted-foreground font-mono font-normal">
                              {' '}
                              · {warning.externalId}
                            </span>
                          ) : null}
                        </p>
                        <p className="text-muted-foreground">{warning.message}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Cronologia</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="space-y-3 text-sm">
                <Row label="Creato" value={formatDateTime(job.createdAt)} />
                <Row label="Avviato" value={formatDateTime(job.startedAt)} />
                <Row label="Concluso" value={formatDateTime(job.finishedAt)} />
                {job.startedAt ? (
                  <Row
                    label="Durata"
                    value={formatDuration(job.startedAt, job.finishedAt)}
                    numeric
                  />
                ) : null}
                {job.catalogVersionAfter ? (
                  <div>
                    <dt className="text-muted-foreground">Versione catalogo</dt>
                    <dd className="mt-0.5 font-mono text-xs break-all">
                      {job.catalogVersionAfter}
                    </dd>
                  </div>
                ) : null}
                {job.createdBy ? (
                  <div>
                    <dt className="text-muted-foreground">Avviato da</dt>
                    <dd className="mt-0.5 font-mono text-xs break-all">{job.createdBy}</dd>
                  </div>
                ) : null}
              </dl>
            </CardContent>
          </Card>
        </div>

        {job.status === 'succeeded' && !job.dryRun ? (
          <Card>
            <CardContent className="flex flex-wrap items-center justify-between gap-3 pt-4 sm:pt-5">
              <p className="text-muted-foreground text-sm">
                Import concluso. Se hai importato senza pubblicare, il batch è da rivedere.
              </p>
              <Button variant="outline" asChild>
                <Link to="/esercizi" search={{ published: false }}>
                  Vedi gli esercizi da rivedere
                </Link>
              </Button>
            </CardContent>
          </Card>
        ) : null}
      </div>
    </>
  )
}

function Row({ label, value, numeric }: { label: string; value: string; numeric?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-muted-foreground shrink-0">{label}</dt>
      <dd className="text-right font-medium" {...(numeric ? { 'data-numeric': true } : {})}>
        {value}
      </dd>
    </div>
  )
}
